export type ShakenMode = "factory" | "user";

export interface AuthContext {
  version: 2;
  loginUserId: string;
  token: string;
  kyotenCd?: string;
  loginTypeKbn?: string;
  isDebug?: boolean;
  groupCd?: string;
  shatenCd?: string;
  // FunctionSettingから所属3項目を取得済みであることを示す。
  // 未取得のログイン直後は他タブの切替判定を保留する。
  organizationReady?: boolean;
  // 最後にPUT /auth/tokenが成功した時刻。タブ間で更新頻度を抑制するために共有する。
  lastSessionRenewedAt?: number;
  updatedAt: number;
}

export interface ReservationContext {
  version: 2;
  code: string;
  uketsukeCd?: string;
  uketsukeExtendsId?: number;
  yoyakuNo?: string;
  savedAt: number;
}

/**
 * ブラウザ保存領域のキーとJSON読み書きを集約する。
 * localStorageには経路内で共有すべき認証情報だけを保存し、
 * 予約・画面状態はsessionStorageへ保存する。
 */
export class ShakenStorage {
  static readonly AUTH_CONTEXT_CHANGED_EVENT = "shaken:auth-context-changed";
  // storage イベントは更新後の値だけを通知するため、別タブからの変更を
  // 判定するには、このタブが最後に使用した認証コンテキストを保持しておく。
  private static activeAuthContexts: { [key in ShakenMode]?: AuthContext | null } = {};

  static getMode(isUserMode?: boolean): ShakenMode {
    const isUser = typeof isUserMode === "boolean"
      ? isUserMode
      : window.location.pathname.indexOf("/user") === 0;
    return isUser ? "user" : "factory";
  }

  static getAuthKey(mode: ShakenMode = ShakenStorage.getMode()): string {
    return "shaken:v2:auth:" + mode;
  }

  static getReservationContextKey(mode: ShakenMode = ShakenStorage.getMode()): string {
    return "shaken:v2:" + mode + ":reservation-detail";
  }

  static getSessionKey(name: string, mode: ShakenMode = ShakenStorage.getMode()): string {
    return "shaken:v2:" + mode + ":" + name;
  }

  static getAuth(mode: ShakenMode = ShakenStorage.getMode()): AuthContext | null {
    return ShakenStorage.readJson<AuthContext>(window.localStorage, ShakenStorage.getAuthKey(mode));
  }

  static getActiveAuth(mode: ShakenMode = ShakenStorage.getMode()): AuthContext | null {
    if (Object.prototype.hasOwnProperty.call(ShakenStorage.activeAuthContexts, mode)) {
      return ShakenStorage.activeAuthContexts[mode] || null;
    }
    const context = ShakenStorage.getAuth(mode);
    if (context) {
      ShakenStorage.activeAuthContexts[mode] = context;
    }
    return context;
  }

  static setActiveAuth(context: AuthContext | null, mode: ShakenMode = ShakenStorage.getMode()): void {
    if (context) {
      ShakenStorage.activeAuthContexts[mode] = context;
    } else {
      // 明示的な破棄後は別タブの認証を自動で取り込まない。
      ShakenStorage.activeAuthContexts[mode] = null;
    }
  }

  static saveAuth(context: AuthContext, mode: ShakenMode = ShakenStorage.getMode()): void {
    ShakenStorage.setActiveAuth(context, mode);
    window.localStorage.setItem(ShakenStorage.getAuthKey(mode), JSON.stringify(context));
    ShakenStorage.notifyActiveAuthChanged(context, mode);
  }

  static updateAuth(
    changes: Partial<AuthContext>,
    mode: ShakenMode = ShakenStorage.getMode()
  ): AuthContext | null {
    const current = ShakenStorage.getActiveAuth(mode);
    if (!current) {
      return null;
    }
    const next: AuthContext = {
      ...current,
      ...changes,
      version: 2,
      updatedAt: Date.now()
    };
    ShakenStorage.saveAuth(next, mode);
    return next;
  }

  static removeAuth(mode: ShakenMode = ShakenStorage.getMode()): void {
    ShakenStorage.setActiveAuth(null, mode);
    window.localStorage.removeItem(ShakenStorage.getAuthKey(mode));
    ShakenStorage.notifyActiveAuthChanged(null, mode);
  }

  static getReservationContext(mode: ShakenMode = ShakenStorage.getMode()): ReservationContext | null {
    const saved = ShakenStorage.readJson<any>(window.sessionStorage, ShakenStorage.getReservationContextKey(mode));
    if (!saved) {
      return null;
    }
    // 初期リリース版が保存した予約詳細全体にも対応する。
    if (saved.version === 2 && saved.code) {
      return saved as ReservationContext;
    }
    const uketsukeCd = saved.uketsukeCd || (saved.uketsuke && saved.uketsuke.uketsukeCd);
    const yoyakuNo = saved.yoyakuNo || (saved.sknUketsukeExtends && saved.sknUketsukeExtends.yoyakuNo);
    const code = mode === "user" ? yoyakuNo : uketsukeCd;
    if (!code) {
      ShakenStorage.removeReservationDetail(mode);
      return null;
    }
    const context: ReservationContext = {
      version: 2,
      code,
      uketsukeCd,
      uketsukeExtendsId: saved.uketsukeExtendsId || (saved.sknUketsukeExtends && saved.sknUketsukeExtends.id),
      yoyakuNo,
      savedAt: Date.now()
    };
    ShakenStorage.saveReservationContext(context, mode);
    return context;
  }

  static saveReservationContext(context: ReservationContext, mode: ShakenMode = ShakenStorage.getMode()): void {
    window.sessionStorage.setItem(
      ShakenStorage.getReservationContextKey(mode),
      JSON.stringify(context)
    );
  }

  static removeReservationDetail(mode: ShakenMode = ShakenStorage.getMode()): void {
    window.sessionStorage.removeItem(ShakenStorage.getReservationContextKey(mode));
  }

  // 予約の復元情報と、その予約に紐づく画面状態を削除する。
  static removeReservationSessionData(mode: ShakenMode = ShakenStorage.getMode()): void {
    ShakenStorage.removeReservationDetail(mode);
    [
      "estimate-current-index", "estimate-current-id", "option-edit-mode",
      "shouhiyou-edit-mode", "coupon-edit-mode", "status-result-tab"
    ].forEach(name => window.sessionStorage.removeItem(ShakenStorage.getSessionKey(name, mode)));

    const seibiPrefix = ShakenStorage.getSessionKey("seibi-edit-mode:", mode);
    for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
      const key = window.sessionStorage.key(i);
      if (key && key.indexOf(seibiPrefix) === 0) {
        window.sessionStorage.removeItem(key);
      }
    }
  }

  private static readJson<T>(storage: Storage, key: string): T | null {
    const value = storage.getItem(key);
    if (!value) {
      return null;
    }
    try {
      return JSON.parse(value) as T;
    } catch (error) {
      storage.removeItem(key);
      return null;
    }
  }

  /** 同一タブではstorageイベントが発火しないため、App用の通知を別途送る。 */
  private static notifyActiveAuthChanged(context: AuthContext | null, mode: ShakenMode): void {
    if (typeof window === "undefined" || typeof window.dispatchEvent !== "function" ||
      typeof CustomEvent !== "function") {
      return;
    }
    window.dispatchEvent(new CustomEvent(ShakenStorage.AUTH_CONTEXT_CHANGED_EVENT, {
      detail: { context, mode }
    }));
  }
}
